import { normalizeDigitsOnly, normalizePartyName } from '../../shared/validators/party-validators';

/** Extract up to `maxLen` letters from a name segment (A–Z only, uppercase). */
export function extractCodeLetters(value: string | null | undefined, maxLen: number): string {
  const letters = (value ?? '').replace(/[^a-zA-Z]/g, '').toUpperCase();
  if (!letters) return '';
  return letters.slice(0, maxLen);
}

/**
 * Auto-generate party code: first 2 letters of first name + first 2 of last name + last 2 mobile digits.
 * Example: Raj Patel, 9876543210 → RAPA10
 */
export function generatePartyCode(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  mobile: string | null | undefined,
): string {
  const prefix = extractCodeLetters(firstName, 2) + extractCodeLetters(lastName, 2);
  const digits = normalizeDigitsOnly(mobile ?? '');
  const suffix = digits.length >= 2 ? digits.slice(-2) : digits;
  return (prefix + suffix).toUpperCase();
}

/**
 * Same formula as Customer/Supplier, splitting a full ledger name on the first space.
 * Example: "Raj Patel", 9876543210 → RAPA10
 */
export function generatePartyCodeFromFullName(
  fullName: string | null | undefined,
  mobile: string | null | undefined,
): string {
  const trimmed = normalizePartyName(fullName ?? '');
  if (!trimmed) return generatePartyCode('', '', mobile);
  const space = trimmed.indexOf(' ');
  const first = space < 0 ? trimmed : trimmed.slice(0, space);
  const last = space < 0 ? '' : trimmed.slice(space + 1).trim();
  return generatePartyCode(first, last, mobile);
}

/** Build display full name from first + last (no stored FullName column). */
export function formatPartyFullName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): string {
  const first = firstName?.trim() ?? '';
  const last = lastName?.trim() ?? '';
  if (first && last) return `${first} ${last}`;
  return first || last;
}

/** List/dropdown label: "Raj Patel - RAPA10" */
export function formatPartyDisplayLabel(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  code: string | null | undefined,
): string {
  const name = formatPartyFullName(firstName, lastName);
  const c = code?.trim() ?? '';
  if (name && c) return `${name} - ${c}`;
  return name || c;
}

/** Initials for avatar from first + last name. */
export function partyInitials(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): string {
  const first = extractCodeLetters(firstName, 1);
  const last = extractCodeLetters(lastName, 1);
  if (first && last) return first + last;
  const combined = extractCodeLetters(formatPartyFullName(firstName, lastName), 2);
  return combined || '??';
}
